import { useState, useEffect, FormEvent } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'

interface Incentive {
  id: number
  childId: number
  name: string
  type: string
  goalValue: number
  currentProgress: number
  rewardText: string
  status: string
  currentStreak: number
  longestStreak: number
  streakGoal: number | null
  streakRewardText: string | null
  lastResetDate: string | null
}

function unitLabel(type: string) {
  if (type === 'DURATION') return 'minutes'
  if (type === 'SPELLING_MASTERED') return 'words mastered'
  return 'exercises'
}

function IncentiveForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: Incentive
  onSave: (values: {
    name: string
    type: string
    goalValue: number
    rewardText: string
    streakGoal: number | null
    streakRewardText: string | null
  }) => Promise<void>
  onCancel: () => void
}) {
  const [name, setName] = useState(initial?.name || '')
  const [type, setType] = useState(initial?.type || 'DURATION')
  const [goalValue, setGoalValue] = useState(initial?.goalValue?.toString() || '')
  const [rewardText, setRewardText] = useState(initial?.rewardText || '')
  const [streakGoal, setStreakGoal] = useState(initial?.streakGoal?.toString() || '')
  const [streakRewardText, setStreakRewardText] = useState(initial?.streakRewardText || '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await onSave({
        name,
        type,
        goalValue: parseInt(goalValue, 10),
        rewardText,
        streakGoal: streakGoal ? parseInt(streakGoal, 10) : null,
        streakRewardText: streakRewardText || null,
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 border border-border rounded-lg space-y-3 bg-card">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div>
        <label className="block text-sm font-medium mb-1">Name</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Daily reading goal"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm"
          >
            <option value="DURATION">Duration (minutes)</option>
            <option value="EXERCISES_COMPLETED">Exercises Completed</option>
            <option value="SPELLING_MASTERED">Spelling Words Mastered</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Daily Goal ({type === 'DURATION' ? 'minutes' : 'exercises'})
          </label>
          <input
            type="number"
            required
            min={1}
            value={goalValue}
            onChange={(e) => setGoalValue(e.target.value)}
            placeholder="15"
            className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Daily Reward</label>
        <input
          type="text"
          required
          value={rewardText}
          onChange={(e) => setRewardText(e.target.value)}
          placeholder="30 min screen time"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
      </div>

      <div className="border-t border-border pt-3 mt-3">
        <p className="text-sm font-medium mb-2">Streak Bonus (optional)</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Days in a row</label>
            <input
              type="number"
              min={2}
              value={streakGoal}
              onChange={(e) => setStreakGoal(e.target.value)}
              placeholder="7"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Streak Reward</label>
            <input
              type="text"
              value={streakRewardText}
              onChange={(e) => setStreakRewardText(e.target.value)}
              placeholder="ice cream trip"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition disabled:opacity-50 min-h-[44px]"
        >
          {submitting ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-accent transition min-h-[44px]"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function IncentiveCard({
  incentive,
  onUpdate,
}: {
  incentive: Incentive
  onUpdate: () => void
}) {
  const [editing, setEditing] = useState(false)

  const handleAction = async (action: string) => {
    try {
      await apiFetch(`/incentives/${incentive.id}/${action}`, { method: 'POST' })
      onUpdate()
    } catch {
      // silently fail
    }
  }

  const handleDelete = async () => {
    try {
      await apiFetch(`/incentives/${incentive.id}`, { method: 'DELETE' })
      onUpdate()
    } catch {
      // silently fail
    }
  }

  const handleEdit = async (values: {
    name: string
    type: string
    goalValue: number
    rewardText: string
    streakGoal: number | null
    streakRewardText: string | null
  }) => {
    await apiFetch(`/incentives/${incentive.id}`, {
      method: 'PUT',
      body: JSON.stringify(values),
    })
    setEditing(false)
    onUpdate()
  }

  if (editing) {
    return <IncentiveForm initial={incentive} onSave={handleEdit} onCancel={() => setEditing(false)} />
  }

  const pct = Math.min(100, Math.round((incentive.currentProgress / incentive.goalValue) * 100))
  const isEarned = incentive.status === 'EARNED'
  const isRedeemed = incentive.status === 'REDEEMED'
  const hasStreakGoal = incentive.streakGoal != null && incentive.streakGoal > 0

  return (
    <div
      className={`p-4 border rounded-lg space-y-3 ${
        isEarned ? 'border-amber-300 bg-amber-50/50' : isRedeemed ? 'border-border opacity-60' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium">{incentive.name}</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {incentive.currentProgress} / {incentive.goalValue} {unitLabel(incentive.type)} today
          </p>
          <p className="text-sm mt-1">
            Reward: <span className="font-medium">{incentive.rewardText}</span>
          </p>
        </div>
        <div className="text-right shrink-0">
          {isEarned && <span className="text-2xl">🏆</span>}
          {incentive.currentStreak > 0 && (
            <p className="text-sm font-medium text-amber-500">
              🔥 {incentive.currentStreak} day streak
            </p>
          )}
        </div>
      </div>

      {incentive.status === 'ACTIVE' && (
        <div className="w-full bg-accent rounded-full h-2.5 overflow-hidden">
          <div
            className="bg-primary h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {hasStreakGoal && (
        <div className="text-sm text-muted-foreground">
          <span className="font-medium">
            Streak goal: {incentive.currentStreak} / {incentive.streakGoal} days
          </span>
          {incentive.streakRewardText && (
            <span> → {incentive.streakRewardText}</span>
          )}
          {incentive.longestStreak > 0 && (
            <span className="ml-2 text-xs">(best: {incentive.longestStreak})</span>
          )}
        </div>
      )}

      <div className="flex gap-2">
        {incentive.status === 'ACTIVE' && (
          <>
            <button
              onClick={() => setEditing(true)}
              className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px]"
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              className="text-xs px-3 py-1.5 text-red-500 border border-red-200 rounded-md hover:bg-red-50 transition min-h-[36px]"
            >
              Delete
            </button>
          </>
        )}
        {isEarned && (
          <button
            onClick={() => handleAction('redeem')}
            className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md font-medium hover:opacity-90 transition min-h-[36px]"
          >
            Mark as Redeemed
          </button>
        )}
        {isRedeemed && (
          <button
            onClick={() => handleAction('reset')}
            className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px]"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  )
}

export default function IncentivesPage() {
  const { childId } = useParams<{ childId: string }>()
  const [incentives, setIncentives] = useState<Incentive[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)

  const fetchIncentives = async () => {
    try {
      const data = await apiFetch<Incentive[]>(`/incentives?childId=${childId}`)
      setIncentives(data)
    } catch {
      setIncentives([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchIncentives()
  }, [childId])

  const handleCreate = async (values: {
    name: string
    type: string
    goalValue: number
    rewardText: string
    streakGoal: number | null
    streakRewardText: string | null
  }) => {
    await apiFetch('/incentives', {
      method: 'POST',
      body: JSON.stringify({ childId: parseInt(childId!, 10), ...values }),
    })
    setShowAdd(false)
    await fetchIncentives()
  }

  const active = incentives.filter((i) => i.status === 'ACTIVE')
  const earned = incentives.filter((i) => i.status === 'EARNED')
  const redeemed = incentives.filter((i) => i.status === 'REDEEMED')

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">Loading incentives...</p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <Link
        to="/dashboard"
        className="text-sm text-muted-foreground hover:text-foreground transition inline-flex items-center gap-1"
      >
        &larr; Back to dashboard
      </Link>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Incentives</h1>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition min-h-[44px]"
        >
          {showAdd ? 'Cancel' : 'Add Incentive'}
        </button>
      </div>

      {showAdd && (
        <IncentiveForm onSave={handleCreate} onCancel={() => setShowAdd(false)} />
      )}

      {active.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Active</h2>
          {active.map((inc) => (
            <IncentiveCard key={inc.id} incentive={inc} onUpdate={fetchIncentives} />
          ))}
        </div>
      )}

      {earned.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-600">Earned</h2>
          {earned.map((inc) => (
            <IncentiveCard key={inc.id} incentive={inc} onUpdate={fetchIncentives} />
          ))}
        </div>
      )}

      {redeemed.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Redeemed</h2>
          {redeemed.map((inc) => (
            <IncentiveCard key={inc.id} incentive={inc} onUpdate={fetchIncentives} />
          ))}
        </div>
      )}

      {incentives.length === 0 && !showAdd && (
        <div className="py-4">
          <MagnumSays>
            <p className="font-medium">No goals set yet!</p>
            <p className="mt-1 text-muted-foreground">
              A daily practice goal goes a long way. Even 10 minutes a day builds real reading skills!
            </p>
          </MagnumSays>
        </div>
      )}
    </div>
  )
}
